"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.queryDegradingRule = void 0;
exports.getDegradingQuery = getDegradingQuery;
const zod_1 = require("zod");
const utils_1 = require("@azimutt/utils");
const common_1 = require("../../common");
const databaseUtils_1 = require("../../databaseUtils");
const date_1 = require("../../helpers/date");
const duration_1 = require("../../helpers/duration");
const percent_1 = require("../../helpers/percent");
const sql_1 = require("../../helpers/sql");
const connector_1 = require("../../interfaces/connector");
const rule_1 = require("../rule");
const ruleId = 'query-degrading';
const ruleName = 'degrading query';
const ruleDescription = 'queries with mean execution time growing over daily/monthly rate thresholds and executions and duration over the thresholds also';
const CustomRuleConf = rule_1.RuleConf.extend({
    ignores: connector_1.QueryId.array().optional(),
    minExec: zod_1.z.number(),
    minDuration: duration_1.Duration,
    maxDegradation: common_1.Percent,
    maxDegradationMonthly: common_1.Percent,
    maxDegradationDaily: common_1.Percent,
}).strict().describe('QueryDegradingConf');
exports.queryDegradingRule = {
    id: ruleId,
    name: ruleName,
    description: ruleDescription,
    conf: { level: rule_1.RuleLevel.enum.high, minExec: 10, minDuration: 100, maxDegradation: 1, maxDegradationMonthly: 0.2, maxDegradationDaily: 0.1 },
    zConf: CustomRuleConf,
    analyze(conf, now, db, queries, history, reference) {
        const refIgnores = reference.map(r => r.extra?.queryId).filter(utils_1.isNotUndefined);
        const ignores = refIgnores.concat(conf.ignores || []);
        const historyQueriesById = history.map(h => ({ report: h.report, date: h.date, queries: (0, utils_1.indexBy)(h.queries, q => q.id) }));
        return queries
            .filter(q => !ignores.some(i => i === q.id))
            .map(query => {
            const previous = historyQueriesById.map(h => {
                const queryHist = h.queries[query.id];
                return queryHist ? { report: h.report, date: h.date, query: queryHist } : undefined;
            }).filter(utils_1.isNotUndefined);
            return getDegradingQuery(now, query, previous, conf.minExec, conf.minDuration, conf.maxDegradation, conf.maxDegradationMonthly, conf.maxDegradationDaily);
        })
            .filter(utils_1.isNotUndefined)
            .sort((a, b) => -(a.daily - b.daily))
            .map(r => {
            const entity = (0, sql_1.getMainEntity)(r.current.query);
            const degradation = `${(0, percent_1.showPercent)(r.degradation)} (${(0, duration_1.showDuration)(r.previous.exec?.meanTime || 0)} to ${(0, duration_1.showDuration)(r.current.exec?.meanTime || 0)})`;
            const rate = r.period > duration_1.oneMonth ? `${(0, percent_1.showPercent)(r.monthly)} monthly, ` : r.period > duration_1.oneDay ? `${(0, percent_1.showPercent)(r.daily)} daily, ` : '';
            return (0, utils_1.removeUndefined)({
                ruleId,
                ruleName,
                ruleLevel: conf.level,
                message: `Query ${r.current.id}${entity ? ` on ${(0, databaseUtils_1.entityRefToId)(entity)}` : ''} degraded mean exec time by ${degradation} since ${(0, date_1.showDate)(r.date)} (${rate}${(0, sql_1.formatSql)(r.current.query)}).`,
                entity,
                extra: {
                    queryId: r.current.id,
                    query: r.current.query,
                    degradationPercent: r.degradation,
                    periodMillis: r.period,
                    previousDate: new Date(r.date).toISOString(),
                    previousReport: r.report,
                    previousStats: queryStats(r.previous),
                    currentStats: queryStats(r.current),
                    entities: (0, sql_1.getEntities)(r.current.query)
                }
            });
        });
    }
};
function queryStats({ id, database, query, ...stats }) {
    return stats;
}
function getDegradingQuery(now, query, history, minExec, minDuration, maxDegradation, maxDegradationMonthly, maxDegradationDaily) {
    const meanTime = query.exec?.meanTime;
    const count = query.exec?.count;
    if (meanTime && meanTime > minDuration && count && count >= minExec) {
        const meanTimeHistory = history.map(h => h.query.exec?.count && h.query.exec?.meanTime ? { report: h.report, date: h.date, query: h.query, count: h.query.exec.count, meanTime: h.query.exec.meanTime } : undefined).filter(utils_1.isNotUndefined);
        const degradations = meanTimeHistory.filter(p => p.count >= minExec).map(previous => {
            const period = now - previous.date;
            const degradation = (0, percent_1.computePercent)(previous.meanTime, meanTime);
            const daily = degradation / (period / duration_1.oneDay);
            const monthly = degradation / (period / duration_1.oneMonth);
            if (degradation >= maxDegradation || (period > duration_1.oneDay && daily >= maxDegradationDaily) || (period > duration_1.oneMonth && monthly >= maxDegradationMonthly)) {
                return { report: previous.report, date: previous.date, previous: previous.query, current: query, degradation, period, daily, monthly };
            }
            else {
                return undefined;
            }
        }).filter(utils_1.isNotUndefined);
        // return maxBy(degradations, d => d.since) // earliest degradation
        return (0, utils_1.maxBy)(degradations, d => d.daily); // worst degradation
    }
    return undefined;
}
//# sourceMappingURL=queryDegrading.js.map